import * as Long from 'long';
import { Logger } from '../metrics/logger.js';
import { MessageSource, InfoKeys, ServerMessageStatus } from '../consts.js';
import { im_proto } from '../../proto/index.js';
import { ContextClassBase } from '../service/RuntimeContext.js';
import { isTrueValue } from '../utils/tools.js';

var PropertyStatus;
(function (PropertyStatus) {
    PropertyStatus[PropertyStatus["Sending"] = 0] = "Sending";
    PropertyStatus[PropertyStatus["Success"] = 1] = "Success";
    PropertyStatus[PropertyStatus["Failed"] = 2] = "Failed";
    // ...
})(PropertyStatus || (PropertyStatus = {}));
/** 消息结构 */
class Message extends ContextClassBase {
    constructor() {
        super(...arguments);
        /** @internal */
        this.indexInConversation = Long.ZERO;
        /** @internal */
        this.orderInConversation = Long.ZERO;
        this.property = {};
        this.source = MessageSource.Unknown;
    }
    /**
     * 消息所属会话类型
     * @{link Conversation.type}
     */
    get conversationType() {
        return this.conversationBizType & 15;
    }
    ;
    set conversationType(newType) {
        this.conversationBizType = newType;
    }
    /**
     * 消息客户端 id
     */
    get clientId() {
        var _a;
        if (this.ext) {
            return (_a = this.ext[InfoKeys.ClientMessageId]) !== null && _a !== void 0 ? _a : '';
        }
        else {
            return '';
        }
    }
    /** @internal */
    set clientId(value) {
        if (!(this.ext)) {
            this.ext = {};
        }
        this.ext[InfoKeys.ClientMessageId] = value;
    }
    /** @internal */
    static createClientMessage(ctx, clientMessage) {
        var _a;
        const msg = new Message(ctx);
        msg.conversationId = clientMessage.conversationId;
        msg.type = clientMessage.type;
        msg.conversationType = clientMessage.conversationType;
        msg.conversationShortId = clientMessage.conversationShortId;
        msg.sender = ctx.option.userId;
        msg.content = clientMessage.content;
        msg.createdAt = new Date(Date.now());
        const newExt = Object.assign({}, clientMessage.ext);
        for (const k of Object.keys(newExt)) {
            if (k.startsWith('s:')) {
                Logger.ctxDebug(msg.ctx, `delete s: prefix ext key: '${k}: ${newExt[k]}'`);
                delete newExt[k];
            }
        }
        msg.ext = newExt;
        msg.mentionedUsers = clientMessage.mentionedUsers;
        msg.clientId = clientMessage.id;
        msg.version = Long.ZERO;
        msg.serverStatus = ServerMessageStatus.Enable;
        msg.isOffline = true;
        if (clientMessage.referenceMessage) {
            let refStatus = im_proto.MessageStatus.AVAILABLE;
            if (clientMessage.referenceMessage.isRecalled) {
                refStatus = im_proto.MessageStatus.RECALLED;
            }
            else if (!clientMessage.referenceMessage.visible) {
                refStatus = im_proto.MessageStatus.INVISIBLE;
            }
            msg.referenceInfo = {
                referenced_message_id: Long.fromValue(clientMessage.referenceMessage.serverId),
                hint: (_a = clientMessage.referenceHint) !== null && _a !== void 0 ? _a : '',
                ref_message_type: Long.fromValue(clientMessage.referenceMessage.type),
                referenced_message_status: refStatus
            };
            // calculate root ref
            if (clientMessage.referenceMessage.referenceInfo !== undefined) { // is more than 1-level ref
                // use the inner root as the root
                msg.referenceInfo.root_message_id = clientMessage.referenceMessage.referenceInfo.root_message_id;
                msg.referenceInfo.root_message_conv_index = clientMessage.referenceMessage.referenceInfo.root_message_conv_index;
            }
            else { // only 1-level ref, we use the reference message itself as root
                msg.referenceInfo.root_message_id = Long.fromValue(clientMessage.referenceMessage.serverId);
                msg.referenceInfo.root_message_conv_index = clientMessage.referenceMessage.indexInConversation;
                // TODO: this message may not send successfully, should we change the original message and mark it as root ref?
            }
        }
        if (msg.ctx.option.secUid) {
            msg.secSender = msg.ctx.option.secUid;
        }
        return msg;
    }
    /** @internal */
    static fromServerMessage(ctx, msg, logid) {
        const message = new Message(ctx);
        message.serverId = msg.server_message_id.toString();
        message.type = msg.message_type;
        message.ext = Object.assign({}, msg.ext);
        message.conversationId = msg.conversation_id;
        message.content = msg.content;
        message.sender = msg.sender.toString();
        message.createdAt = new Date(msg.create_time.toNumber());
        message.indexInConversation = msg.index_in_conversation;
        message.orderInConversation = msg.order_in_conversation; // FIXME: 已存在不更新
        message.serverStatus = msg.status;
        message.conversationShortId = msg.conversation_short_id.toString();
        message.conversationType = msg.conversation_type;
        message.version = msg.version;
        message.secSender = msg.sec_sender;
        message.isOffline = false;
        message.property = Message.fromServerProperty(msg);
        if (msg.reference_info) {
            message.referenceInfo = msg.reference_info;
        }
        if (logid !== undefined && message.ctx.option.debug) {
            message.ext[InfoKeys.LocalLogId] = logid;
        }
        return message;
    }
    static fromServerProperty(msg) {
        const result = {};
        if (!msg.property_list) {
            return result;
        }
        for (const key of Object.keys(msg.property_list)) {
            const current = msg.property_list[key];
            if (current && current.Items) {
                result[key] = current.Items.map(p => {
                    var _a, _b, _c, _d, _e;
                    return ({
                        messageId: (_a = msg.ext[InfoKeys.ClientMessageId]) !== null && _a !== void 0 ? _a : '',
                        conversationId: msg.conversation_id,
                        key,
                        userId: (_b = p.uid) === null || _b === void 0 ? void 0 : _b.toString(),
                        secUid: (_c = p.sec_uid) !== null && _c !== void 0 ? _c : '',
                        createTime: new Date(((_e = (_d = p.create_time) === null || _d === void 0 ? void 0 : _d.toNumber()) !== null && _e !== void 0 ? _e : 0) * 1000),
                        idempotentId: p.idempotent_id,
                        value: p.value,
                        version: msg.version,
                        status: PropertyStatus.Success
                    });
                });
            }
        }
        return result;
    }
    /** @internal exist 的消息 merge 新的消息 */
    merge(newMsg) {
        if (newMsg && this.type === newMsg.type) {
            // 合并到新的消息 ext 里面, 这样可以在后面 assign 的时候再写上去
            newMsg.ext = Object.assign(this.ext || {}, newMsg.ext);
            // 空content保护
            if (newMsg.content === '' && this.content) {
                Logger.ctxDebug(this.ctx, 'ignore empty content replace:', newMsg);
                newMsg.content = this.content;
            }
            if (newMsg.clientId === this.clientId) {
                // Logger.ctxDebug(this.ctx, `merge same message: ${this.clientId}, fixing index`);
                if (newMsg.indexInConversation.lt(this.indexInConversation)) {
                    this.indexInConversation = newMsg.indexInConversation;
                    return this;
                }
            }
            Object.assign(this, newMsg);
        }
        return this;
    }
    /** 是否是一条普通消息, 区别于 Command 这种特殊消息 */
    get isNormalMsg() {
        return this.type < 50000;
    }
    /** 是否是一条命令消息 (type === 50001) https://bytedance.feishu.cn/wiki/wikcn4eYosDsJRvqykKCGNgKjxg#sZU956 */
    get isCommandMsg() {
        return this.type === im_proto.MessageType.MESSAGE_TYPE_COMMAND;
    }
    /** 是否是一条特殊消息 (type >= 50000) */
    get isSpecialMessage() {
        return this.type >= 50000;
    }
    /** 消息是否对自己可见 */
    get visible() {
        // 兜住 undefined 的情况
        if (this.serverStatus === ServerMessageStatus.Disable) {
            return false;
        }
        // 特殊消息, 不可见
        if (this.isSpecialMessage) {
            return false;
        }
        // 传入的 userid 不合法或没有 ext 字段，默认为对自己可见
        if (!this.ctx.option.userId || !this.ext) {
            return true;
        }
        // server 逻辑, 先检查 visible
        // 在不为 0 的情况下找, 找到了则直接采用当前情况, 没找到采用反面情况
        // 感觉应该如果找不到 fallback 到另一个会更好, 如果同时填了这两个字段?
        if (this.ext[InfoKeys.MessageVisible] && this.ext[InfoKeys.MessageVisible].length > 0) {
            return this.ext[InfoKeys.MessageVisible].split(',').indexOf(this.ctx.option.userId) !== -1;
        }
        if (this.ext[InfoKeys.MessageInvisible] && this.ext[InfoKeys.MessageInvisible].length > 0) {
            return this.ext[InfoKeys.MessageInvisible].split(',').indexOf(this.ctx.option.userId) === -1;
        }
        // 没有特殊字段, 认为消息可见
        return true;
    }
    /** 消息是否不需要被顶出 */
    get unpop() {
        if (!this.ext) {
            return false;
        }
        if (!this.visible || isTrueValue(this.ext, InfoKeys.DoNotPopConversation)) {
            return true;
        }
        return false;
    }
    get increaseUnread() {
        if (this.isFromMe || !this.visible || this.isSpecialMessage || this.isRecalled) {
            return false;
        }
        if (isTrueValue(this.ext, InfoKeys.DoNotIncreaseUnread)) {
            return false;
        }
        return true;
    }
    /** 消息是否是自己发送的 */
    get isFromMe() {
        return this.sender === this.ctx.option.userId;
    }
    /** 消息是否被撤回 */
    get isRecalled() {
        return this.ext && !!this.ext[InfoKeys.IsRecalled];
    }
    get isMentioned() {
        // @all should not include @self
        return this.visible && (this.mentionedUsers.includes(this.ctx.option.userId) || (this.mentionedUsers.includes('0') && !this.isFromMe));
    }
    /** 消息里包含的被 @ 用户 */
    get mentionedUsers() {
        if (this.ext && this.ext[InfoKeys.MentionedUser]) {
            return this.ext[InfoKeys.MentionedUser].split(',');
        }
        else {
            return [];
        }
    }
    /** @internal */
    set mentionedUsers(v) {
        if (!this.ext) {
            this.ext = {};
        }
        this.ext[InfoKeys.MentionedUser] = v.join(',');
    }
    get isStrangerMessage() {
        if (!this.ext) {
            return true;
        }
        return ['1', '2', '3'].includes(this.ext[InfoKeys.MessageMode]);
    }
    get isRootReference() {
        if (!this.ext) {
            return false;
        }
        return isTrueValue(this.ext, InfoKeys.IsRootReference);
    }
    get moveReadIndex() {
        if (!this.ext) {
            return true;
        }
        if (!this.visible || this.isFromMe || this.isSpecialMessage) {
            return false;
        }
        if (isTrueValue(this.ext, InfoKeys.DoNotMoveReadIndex)) {
            return false;
        }
        return true;
    }
    get updateLastMessage() {
        if (!this.visible || this.isSpecialMessage) {
            return false;
        }
        if (isTrueValue(this.ext, InfoKeys.DoNotUpdateLastMessage)) {
            return false;
        }
        return true;
    }
}

export { Message, PropertyStatus };
//# sourceMappingURL=Message.js.map
